import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, Table, Form, Row, Col, Spinner, Alert, Tabs, Tab, Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';

const Marge = () => {
  const [bills, setBills] = useState([]);
  const [filteredBills, setFilteredBills] = useState([]);
  const [products, setProducts] = useState([]);
  const [technicalSheets, setTechnicalSheets] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    dateRange: 'today' // today, yesterday, thisWeek, thisMonth, custom
  });
  const [activeTab, setActiveTab] = useState('all');

  const currentUser = useSelector((state) => state.AuthReducer?.user);

  const extractLocId = (loc) => {
    if (!loc) return null;
    if (typeof loc === "string") return loc;
    if (loc.$oid) return loc.$oid;
    if (loc._id) {
      if (typeof loc._id === 'string') return loc._id;
      if (loc._id.$oid) return loc._id.$oid;
      return loc._id.toString();
    }
    return loc.toString ? loc.toString() : null;
  };

  // 📊 Charger toutes les données
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem("token");

      const locationsRes = await axios.get('/api/Locationspin/getAllLocations', {
        headers: { Authorised: token }
      });
      setLocations(locationsRes.data || []);

      let billsUrl = '/api/Bills/getAllBills';
      let productsUrl = '/api/Product/getProduct';
      let sheetsUrl = '/api/TechnicalSheet/all-sheets';

      const [billsRes, productsRes, sheetsRes] = await Promise.all([
        axios.get(billsUrl, { headers: { Authorised: token } }),
        axios.get(productsUrl, { headers: { Authorised: token } }),
        axios.get(sheetsUrl, { headers: { Authorised: token } })
      ]);

      setBills(billsRes.data || []);
      setFilteredBills(billsRes.data || []);
      setProducts(productsRes.data || []);
      setTechnicalSheets(sheetsRes.data || []);

    } catch (err) {
      console.error("❌ Erreur chargement:", err);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      fetchAllData();
    }
  }, [fetchAllData, currentUser]);

  // Fonction pour appliquer les filtres de date
  const applyDateFilters = useCallback(() => {
    if (!bills || bills.length === 0) {
      setFilteredBills([]);
      return;
    }

    let filtered = [...bills];

    // Appliquer le filtre de plage de dates
    if (filters.startDate && filters.endDate) {
      filtered = filtered.filter(bill => {
        if (!bill.date) return false;
        
        try {
          const billDate = new Date(bill.date);
          const startDate = new Date(filters.startDate);
          const endDate = new Date(filters.endDate);
          
          // Définir les heures pour couvrir toute la journée
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(23, 59, 59, 999);
          billDate.setHours(0, 0, 0, 0);
          
          return billDate >= startDate && billDate <= endDate;
        } catch (error) {
          return false;
        }
      });
    } else if (filters.dateRange !== 'custom') {
      const today = new Date();
      
      switch (filters.dateRange) {
        case 'today':
          const todayStart = new Date(today);
          todayStart.setHours(0, 0, 0, 0);
          const todayEnd = new Date(today);
          todayEnd.setHours(23, 59, 59, 999);
          
          filtered = filtered.filter(bill => {
            if (!bill.date) return false;
            const billDate = new Date(bill.date);
            return billDate >= todayStart && billDate <= todayEnd;
          });
          break;
          
        case 'yesterday':
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          const yesterdayStart = new Date(yesterday);
          yesterdayStart.setHours(0, 0, 0, 0);
          const yesterdayEnd = new Date(yesterday);
          yesterdayEnd.setHours(23, 59, 59, 999);
          
          filtered = filtered.filter(bill => {
            if (!bill.date) return false;
            const billDate = new Date(bill.date);
            return billDate >= yesterdayStart && billDate <= yesterdayEnd;
          });
          break;
          
        case 'thisWeek':
          const startOfWeek = new Date(today);
          startOfWeek.setDate(today.getDate() - today.getDay());
          startOfWeek.setHours(0, 0, 0, 0);
          const endOfWeek = new Date(startOfWeek);
          endOfWeek.setDate(startOfWeek.getDate() + 6);
          endOfWeek.setHours(23, 59, 59, 999);
          
          filtered = filtered.filter(bill => {
            if (!bill.date) return false;
            const billDate = new Date(bill.date);
            return billDate >= startOfWeek && billDate <= endOfWeek;
          });
          break;
          
        case 'thisMonth':
          const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          startOfMonth.setHours(0, 0, 0, 0);
          const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
          endOfMonth.setHours(23, 59, 59, 999);
          
          filtered = filtered.filter(bill => {
            if (!bill.date) return false;
            const billDate = new Date(bill.date);
            return billDate >= startOfMonth && billDate <= endOfMonth;
          });
          break;
          
        default:
          // Pas de filtre
          break;
      }
    }

    setFilteredBills(filtered);
  }, [bills, filters]);

  useEffect(() => {
    applyDateFilters();
  }, [applyDateFilters]);

  // 🔥 FONCTION CORRIGÉE: Trouver la localisation d'un produit
  const findProductLocation = useCallback((productId) => {
    if (!productId) return null;

    const productIdStr = extractLocId(productId);

    // 1. Chercher comme sous-produit
    for (const product of products) {
      if (product.subProducts) {
        const subProduct = product.subProducts.find(sp => 
          extractLocId(sp._id) === productIdStr
        );
        if (subProduct) {
          return extractLocId(subProduct.localisation);
        }
      }
    }

    // 2. Chercher comme produit principal
    const mainProduct = products.find(p => 
      extractLocId(p._id) === productIdStr
    );
    if (mainProduct) {
      return extractLocId(mainProduct.localisation);
    }

    return null;
  }, [products]);

  // 🔍 Trouver le nom d'un produit
  const findProductNameById = useCallback((productId) => {
    if (!productId) return "Produit inconnu";

    const productIdStr = extractLocId(productId);

    for (const product of products) {
      if (product.subProducts) {
        const subProduct = product.subProducts.find(sp => 
          extractLocId(sp._id) === productIdStr
        );
        if (subProduct) return subProduct.name;
      }
    }

    const mainProduct = products.find(p => 
      extractLocId(p._id) === productIdStr
    );
    if (mainProduct) return mainProduct.name;

    return "Produit inconnu";
  }, [products]);

  // 🔥 Trouver le coût de production
  const findProductionCost = useCallback((productId) => {
    if (!productId) return 0;

    const productIdStr = extractLocId(productId);

    const sheet = technicalSheets.find(sheet => 
      extractLocId(sheet.subProduct) === productIdStr ||
      extractLocId(sheet.product) === productIdStr
    );

    if (sheet) {
      return sheet.totalCost || 0;
    }

    // Coût estimé à 70% du prix de vente
    let defaultCost = 0;
    for (const product of products) {
      const subProduct = product.subProducts?.find(sp => 
        extractLocId(sp._id) === productIdStr
      );
      if (subProduct && subProduct.price) {
        defaultCost = subProduct.price * 0.7;
        break;
      }
    }

    return defaultCost;
  }, [technicalSheets, products]);

  // 🕒 Formater l'heure à partir de la date
  const formatTimeFromDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('fr-FR', { 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      return "Heure inconnue";
    }
  };

  // 📈 CALCUL DES MARGES - VERSION AVEC GROUPEMENT PAR LOCALISATION
  const calculateMargins = useCallback(() => {
    console.log("=== CALCUL MARGES AVEC GROUPEMENT PAR LOCALISATION ===");

    const marginsByLocation = {};
    let globalTotals = { sales: 0, cost: 0, margin: 0, percentage: 0 };

    if (filteredBills.length === 0) {
      return {
        byLocation: {},
        globalTotals
      };
    }

    filteredBills.forEach(bill => {
      if (!bill.items || !Array.isArray(bill.items)) return;

      bill.items.forEach(item => {
        const productName = findProductNameById(item.id);
        const productLocationId = findProductLocation(item.id);
        
        if (!productLocationId) return;

        const locationName = locations.find(loc => 
          extractLocId(loc._id) === productLocationId
        )?.name || `Localisation ${productLocationId}`;

        // Initialiser la localisation si elle n'existe pas
        if (!marginsByLocation[productLocationId]) {
          marginsByLocation[productLocationId] = {
            name: locationName,
            id: productLocationId,
            sales: 0,
            cost: 0,
            margin: 0,
            percentage: 0,
            items: []
          };
        }

        const sellingPrice = item.price || 0;
        const productionCost = findProductionCost(item.id);
        const quantity = item.quantity || 1;
        const salesAmount = sellingPrice * quantity;
        const costAmount = productionCost * quantity;
        const margin = salesAmount - costAmount;

        // Ajouter aux totaux de la localisation
        marginsByLocation[productLocationId].sales += salesAmount;
        marginsByLocation[productLocationId].cost += costAmount;
        marginsByLocation[productLocationId].margin += margin;

        // Ajouter l'item avec l'heure
        marginsByLocation[productLocationId].items.push({
          time: formatTimeFromDate(bill.date),
          product: productName,
          quantity: quantity,
          sellingPrice: sellingPrice,
          productionCost: productionCost,
          salesAmount: salesAmount,
          costAmount: costAmount,
          margin: margin,
          marginPercentage: salesAmount > 0 ? (margin / salesAmount) * 100 : 0,
          tableNumber: bill.tableNumber,
          billDate: bill.date // Garder la date complète pour le tri
        });

        // Ajouter aux totaux globaux
        globalTotals.sales += salesAmount;
        globalTotals.cost += costAmount;
        globalTotals.margin += margin;
      });
    });

    // Calculer les pourcentages et trier les items par heure
    Object.keys(marginsByLocation).forEach(locationId => {
      const locationData = marginsByLocation[locationId];
      locationData.percentage = locationData.sales > 0 ? 
        (locationData.margin / locationData.sales) * 100 : 0;
      
      // Trier les items par heure (plus récent en premier)
      locationData.items.sort((a, b) => new Date(b.billDate) - new Date(a.billDate));
    });

    // Calculer le pourcentage global
    globalTotals.percentage = globalTotals.sales > 0 ? 
      (globalTotals.margin / globalTotals.sales) * 100 : 0;

    return {
      byLocation: marginsByLocation,
      globalTotals
    };
  }, [filteredBills, locations, findProductNameById, findProductLocation, findProductionCost]);

  const marginsData = calculateMargins();

  // 📊 Obtenir les statistiques pour une localisation spécifique
  const getLocationStats = (locationId) => {
    if (locationId === 'all') {
      return marginsData.globalTotals;
    }
    
    const locationData = marginsData.byLocation[locationId];
    if (!locationData) {
      return { sales: 0, cost: 0, margin: 0, percentage: 0, itemCount: 0 };
    }

    return {
      sales: locationData.sales,
      cost: locationData.cost,
      margin: locationData.margin,
      percentage: locationData.percentage,
      itemCount: locationData.items.length,
      locationName: locationData.name
    };
  };

  // 🎯 Obtenir le nom de la localisation active
  const getActiveLocationName = () => {
    if (activeTab === 'all') {
      return "Toutes les localisations";
    }
    
    const locationData = marginsData.byLocation[activeTab];
    return locationData ? locationData.name : "Localisation inconnue";
  };

  // 🎯 Obtenir le nombre total d'items pour la localisation active
  const getActiveItemCount = () => {
    if (activeTab === 'all') {
      return Object.values(marginsData.byLocation).reduce(
        (sum, loc) => sum + loc.items.length, 0
      );
    }
    
    const locationData = marginsData.byLocation[activeTab];
    return locationData ? locationData.items.length : 0;
  };

  // 🎯 Obtenir le nombre de localisations actives
  const getActiveLocationCount = () => {
    if (activeTab === 'all') {
      return Object.keys(marginsData.byLocation).length;
    }
    return 1;
  };

  // 🎯 Filtrer les données selon l'onglet actif
  const getFilteredData = () => {
    if (activeTab === 'all') {
      return marginsData.byLocation;
    }
    
    // Retourner seulement la localisation sélectionnée
    const filtered = {};
    if (marginsData.byLocation[activeTab]) {
      filtered[activeTab] = marginsData.byLocation[activeTab];
    }
    return filtered;
  };

  const filteredData = getFilteredData();
  const currentStats = getLocationStats(activeTab);
  const currentLocationName = getActiveLocationName();
  const currentItemCount = getActiveItemCount();
  const currentLocationCount = getActiveLocationCount();

  // Gestion des changements de filtre
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      // Si on change de dateRange, réinitialiser les dates personnalisées
      ...(name === 'dateRange' && value !== 'custom' ? { startDate: '', endDate: '' } : {})
    }));
  };

  // Réinitialiser les filtres
  const resetFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      dateRange: 'today'
    });
    setFilteredBills(bills);
  };

  // Formater la date pour l'affichage
  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };

  // Obtenir la description de la plage de dates
  const getDateRangeDescription = () => {
    switch (filters.dateRange) {
      case 'today':
        return `Aujourd'hui (${formatDisplayDate(new Date())})`;
      case 'yesterday':
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        return `Hier (${formatDisplayDate(yesterday)})`;
      case 'thisWeek':
        return 'Cette semaine';
      case 'thisMonth':
        return 'Ce mois';
      case 'custom':
        if (filters.startDate && filters.endDate) {
          return `${formatDisplayDate(filters.startDate)} au ${formatDisplayDate(filters.endDate)}`;
        }
        return 'Période personnalisée';
      default:
        return 'Aujourd\'hui';
    }
  };

  const getAvailableLocations = () => {
    if (!currentUser) return [];
    
    if (currentUser.role === "admin") {
      return locations;
    } else {
      const userLocId = extractLocId(currentUser.localisation?._id);
      return locations.filter(loc => extractLocId(loc._id) === userLocId);
    }
  };

  const availableLocations = getAvailableLocations();

  if (!currentUser) {
    return (
      <div className="text-center mt-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </Spinner>
        <p className="mt-2">Chargement des données utilisateur...</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center mt-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </Spinner>
        <p className="mt-2">Calcul des marges en cours...</p>
      </div>
    );
  }

  return (
    <div className="marge-container" style={{ 
      height: '100vh', 
      overflow: 'auto',
      padding: '20px'
    }}>
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">📈 État des Marges</h2>
            <small className="text-muted">
              Utilisateur: {currentUser?.name} | Rôle: {currentUser?.role}
            </small>
          </div>
          <div className="text-end">
            <span className="badge bg-primary fs-6">
              🏪 {currentLocationName}
            </span>
          </div>
        </div>
        
        {error && (
          <Alert variant="warning" className="mb-4">
            {error}
          </Alert>
        )}

        {/* Section Filtres de Date */}
        <Card className="mb-4">
          <Card.Header className="bg-light">
            <h5 className="mb-0">📅 Filtres de Date</h5>
          </Card.Header>
          <Card.Body>
            <Row className="align-items-center">
              <Col md={3}>
                <Form.Group controlId="dateRange">
                  <Form.Label>Période</Form.Label>
                  <Form.Select 
                    name="dateRange" 
                    value={filters.dateRange} 
                    onChange={handleFilterChange}
                  >
                    <option value="today">Aujourd'hui</option>
                    <option value="yesterday">Hier</option>
                    <option value="thisWeek">Cette semaine</option>
                    <option value="thisMonth">Ce mois</option>
                    <option value="custom">Période personnalisée</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              
              {filters.dateRange === 'custom' && (
                <>
                  <Col md={3}>
                    <Form.Group controlId="startDate">
                      <Form.Label>Date de début</Form.Label>
                      <Form.Control 
                        type="date" 
                        name="startDate" 
                        value={filters.startDate} 
                        onChange={handleFilterChange}
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={3}>
                    <Form.Group controlId="endDate">
                      <Form.Label>Date de fin</Form.Label>
                      <Form.Control 
                        type="date" 
                        name="endDate" 
                        value={filters.endDate} 
                        onChange={handleFilterChange}
                      />
                    </Form.Group>
                  </Col>
                </>
              )}
              
              <Col md={filters.dateRange === 'custom' ? 3 : 9} className="text-end">
                <div className="d-flex justify-content-end align-items-center h-100">
                  <span className="text-muted me-3">
                    {getDateRangeDescription()} • {filteredBills.length} facture(s)
                  </span>
                  <Button 
                    variant="outline-secondary" 
                    onClick={resetFilters}
                    size="sm"
                  >
                    Réinitialiser
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Résumé Dynamique selon la localisation sélectionnée */}
        {filteredBills.length > 0 && (
          <Card className="mb-4 bg-light">
            <Card.Body>
              <Row>
                <Col md={3} className="text-center">
                  <h6>Chiffre d'Affaires {activeTab !== 'all' && currentLocationName}</h6>
                  <h4 className="text-primary">{currentStats.sales.toFixed(2)} DT</h4>
                </Col>
                <Col md={3} className="text-center">
                  <h6>Coût Production {activeTab !== 'all' && currentLocationName}</h6>
                  <h4 className="text-danger">{currentStats.cost.toFixed(2)} DT</h4>
                </Col>
                <Col md={3} className="text-center">
                  <h6>Marge {activeTab !== 'all' && currentLocationName}</h6>
                  <h4 className={currentStats.margin >= 0 ? "text-success" : "text-danger"}>
                    {currentStats.margin.toFixed(2)} DT
                  </h4>
                </Col>
                <Col md={3} className="text-center">
                  <h6>% Marge {activeTab !== 'all' && currentLocationName}</h6>
                  <h4 className={currentStats.percentage >= 0 ? "text-success" : "text-danger"}>
                    {currentStats.percentage.toFixed(1)}%
                  </h4>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col className="text-center">
                  <small className="text-muted">
                    Période: {getDateRangeDescription()} • 
                    {currentLocationCount} localisation(s) • 
                    {currentItemCount} vente(s) 
                    {activeTab !== 'all' && ` • ${currentLocationName}`}
                  </small>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* Onglets de Navigation */}
        <Card className="mb-4">
          <Card.Body style={{ padding: '0' }}>
            <Tabs
              activeKey={activeTab}
              onSelect={(tab) => setActiveTab(tab)}
              className="mb-3"
              fill
            >
              <Tab 
                eventKey="all" 
                title={
                  <span>
                    📊 Toutes les Localisations
                    <small className="ms-1">
                      ({Object.values(marginsData.byLocation).reduce(
                        (sum, loc) => sum + loc.items.length, 0
                      )} ventes)
                    </small>
                  </span>
                }
              >
                {/* Le contenu de tous les onglets est géré en dessous */}
              </Tab>
              {Object.keys(marginsData.byLocation).map(locationId => {
                const locationData = marginsData.byLocation[locationId];
                const stats = getLocationStats(locationId);
                return (
                  <Tab 
                    key={locationId} 
                    eventKey={locationId} 
                    title={
                      <span>
                        🏪 {locationData.name}
                        <small className="ms-1">
                          ({stats.itemCount} ventes)
                        </small>
                      </span>
                    }
                  >
                    {/* Le contenu de chaque onglet est géré en dessous */}
                  </Tab>
                );
              })}
            </Tabs>
          </Card.Body>
        </Card>

        {/* Tableaux par Localisation */}
        {Object.keys(filteredData).length > 0 ? (
          Object.keys(filteredData).map(locationId => {
            const locationData = filteredData[locationId];
            const stats = getLocationStats(locationId);

            return (
              <Card key={locationId} className="mb-4">
                <Card.Header className="bg-primary text-white">
                  <h5 className="mb-0">
                    🏪 {locationData.name}
                    <span className="float-end">
                      Marge: {stats.margin.toFixed(2)} DT ({stats.percentage.toFixed(1)}%)
                    </span>
                  </h5>
                </Card.Header>
                <Card.Body style={{ padding: '0' }}>
                  {/* Résumé de la localisation */}
                  <div className="p-3 bg-light border-bottom">
                    <Row>
                      <Col md={3} className="text-center">
                        <strong>CA: {stats.sales.toFixed(2)} DT</strong>
                      </Col>
                      <Col md={3} className="text-center">
                        <strong>Coût: {stats.cost.toFixed(2)} DT</strong>
                      </Col>
                      <Col md={3} className="text-center">
                        <strong className={stats.margin >= 0 ? "text-success" : "text-danger"}>
                          Marge: {stats.margin.toFixed(2)} DT
                        </strong>
                      </Col>
                      <Col md={3} className="text-center">
                        <strong>{stats.itemCount} ventes</strong>
                      </Col>
                    </Row>
                  </div>

                  {/* Tableau des ventes */}
                  <div className="table-responsive" style={{ maxHeight: '400px', overflow: 'auto' }}>
                    <Table striped bordered hover size="sm" className="mb-0">
                      <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
                        <tr>
                          <th>Heure</th>
                          <th>Table</th>
                          <th>Produit</th>
                          <th>Qté</th>
                          <th>Prix Vente</th>
                          <th>Coût Prod.</th>
                          <th>CA</th>
                          <th>Coût Total</th>
                          <th>Marge</th>
                          <th>%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {locationData.items.map((item, index) => (
                          <tr key={index}>
                            <td>
                              <small className="text-muted">{item.time}</small>
                            </td>
                            <td>Table {item.tableNumber}</td>
                            <td>{item.product}</td>
                            <td>{item.quantity}</td>
                            <td>{item.sellingPrice.toFixed(2)} DT</td>
                            <td>{item.productionCost.toFixed(2)} DT</td>
                            <td>{item.salesAmount.toFixed(2)} DT</td>
                            <td>{item.costAmount.toFixed(2)} DT</td>
                            <td className={item.margin >= 0 ? "text-success" : "text-danger"}>
                              {item.margin.toFixed(2)} DT
                            </td>
                            <td className={item.marginPercentage >= 0 ? "text-success" : "text-danger"}>
                              {item.marginPercentage.toFixed(1)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            );
          })
        ) : (
          <Alert variant="info" className="text-center">
            Aucune donnée de vente disponible pour la période sélectionnée.
          </Alert>
        )}

        {filteredBills.length === 0 && !loading && (
          <Alert variant="info" className="text-center">
            Aucune donnée de vente disponible pour la période sélectionnée.
          </Alert>
        )}
      </div>
    </div>
  );
};

export default Marge;